import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { QUERY_KEYS } from "@/hooks/use-queries";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  Users,
  Heart,
  Gift,
  Building2,
  Search,
  Edit,
  Eye,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Wallet,
  BellRing,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { User } from "@shared/schema";
import { StatCard } from "./shared";

interface ActivationStats {
  totalPatients: number;
  patientsWithWallet: number;
  activationRate: number;
  employerEnrolledPatients: number;
  employerEnrolledWithWallet: number;
  employerActivationRate: number;
  day3NudgeTargets: number;
  needsActivationCount: number;
}

export function UsersDashboard({ users }: { users: User[] }) {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  const { data: activationStats } = useQuery<ActivationStats>({
    queryKey: ["/api/admin/activation-stats"],
  });

  const [editUser, setEditUser] = useState<User | null>(null);
  const [viewUser, setViewUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ firstName: "", lastName: "", email: "", role: "", language: "" });

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, string> }) => {
      return apiRequest("PATCH", `/api/admin/users/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminUsers });
      toast({ title: "User Updated", description: "User details have been saved successfully." });
      setEditUser(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const openEditDialog = (user: User) => {
    setEditForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      role: user.role || "patient",
      language: user.language || "en",
    });
    setEditUser(user);
  };

  const handleEditSubmit = () => {
    if (!editUser) return;
    updateUserMutation.mutate({ id: editUser.id, data: editForm });
  };
  
  const usersByRole = {
    patient: users.filter(u => u.role === "patient").length,
    supporter: users.filter(u => u.role === "supporter").length,
    partners: users.filter(u => u.role === "hospital_partner_admin" || u.role === "employer_admin").length,
  };

  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (u.firstName || "").toLowerCase().includes(q) ||
      (u.lastName || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.username || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / perPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice((safePage - 1) * perPage, safePage * perPage);

  const getRoleBadge = (role: string) => {
    switch (role) {
      case "admin":
        return <Badge variant="destructive">{role}</Badge>;
      case "patient":
        return <Badge variant="default">{role}</Badge>;
      case "supporter":
        return <Badge variant="secondary">{role}</Badge>;
      case "hospital_partner_admin":
        return <Badge variant="outline" className="border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400">{role}</Badge>;
      case "employer_admin":
        return <Badge variant="outline" className="border-purple-300 text-purple-700 dark:border-purple-700 dark:text-purple-400">{role}</Badge>;
      default:
        return <Badge variant="secondary">{role}</Badge>;
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      patient: "Patient",
      supporter: "Supporter",
      driver: "Driver",
      dispatcher: "Dispatcher",
      admin: "Admin",
      hospital_partner_admin: "Hospital Partner Admin",
      employer_admin: "Employer Admin",
    };
    return labels[role] || role;
  };

  const getLanguageLabel = (lang: string) => {
    const labels: Record<string, string> = { en: "English", es: "Spanish", so: "Somali", hmn: "Hmong" };
    return labels[lang] || lang;
  };

  return (
    <>
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard title="Total Users" value={users.length} description="All registered users" icon={Users} color="primary" />
        <StatCard title="Patients" value={usersByRole.patient} description="Patient accounts" icon={Heart} color="blue" />
        <StatCard title="Supporters" value={usersByRole.supporter} description="Supporter accounts" icon={Gift} color="green" />
        <StatCard title="Partners" value={usersByRole.partners} description="Hospital & employer admins" icon={Building2} color="purple" />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-4 w-4 text-primary" />
            Activation Rate
          </CardTitle>
          <CardDescription>Patients who have created at least one wallet</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div data-testid="stat-total-patients" className="rounded-md border p-4">
              <p className="text-xs text-muted-foreground mb-1">Total Patients</p>
              <p className="text-2xl font-bold">{activationStats?.totalPatients ?? "—"}</p>
            </div>
            <div data-testid="stat-patients-with-wallet" className="rounded-md border p-4">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Wallet className="h-3 w-3" /> With Wallet
              </p>
              <p className="text-2xl font-bold">
                {activationStats?.patientsWithWallet ?? "—"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {activationStats ? `${activationStats.activationRate}% activation` : ""}
              </p>
            </div>
            <div data-testid="stat-employer-enrolled" className="rounded-md border p-4">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Building2 className="h-3 w-3" /> Employer-Enrolled
              </p>
              <p className="text-2xl font-bold">
                {activationStats?.employerEnrolledPatients ?? "—"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {activationStats ? `${activationStats.employerActivationRate}% have wallets` : ""}
              </p>
            </div>
            <div data-testid="stat-needs-activation" className="rounded-md border p-4">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <BellRing className="h-3 w-3" /> Needs Outreach
              </p>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {activationStats?.needsActivationCount ?? "—"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {activationStats ? `${activationStats.day3NudgeTargets} in day-3 nudge window` : ""}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle>User Management</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="pl-9 w-full sm:w-60"
                data-testid="input-search-users"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Language</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedUsers.map((user) => (
                <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                  <TableCell className="font-medium">{user.firstName} {user.lastName}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell className="font-mono text-sm">{user.username}</TableCell>
                  <TableCell>{getRoleBadge(user.role || "patient")}</TableCell>
                  <TableCell>{user.language || "en"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(user)}
                        data-testid={`button-edit-user-${user.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setViewUser(user)}
                        data-testid={`button-view-user-${user.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
          <div className="flex items-center justify-between gap-2 mt-4 flex-wrap">
            <p className="text-sm text-muted-foreground">
              Showing {(safePage - 1) * perPage + 1}-{Math.min(safePage * perPage, filteredUsers.length)} of {filteredUsers.length} users
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={safePage <= 1}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                data-testid="button-prev-page"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">Page {safePage} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage >= totalPages}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                data-testid="button-next-page"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
        <DialogContent data-testid="dialog-edit-user">
          <DialogHeader>
            <DialogTitle data-testid="dialog-title-edit-user">Edit User</DialogTitle>
            <DialogDescription>Update user details for {editUser?.username}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={editForm.firstName}
                  onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                  data-testid="input-edit-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={editForm.lastName}
                  onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                  data-testid="input-edit-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                data-testid="input-edit-email"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editForm.role} onValueChange={(val) => setEditForm(prev => ({ ...prev, role: val }))}>
                  <SelectTrigger data-testid="select-edit-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="patient">Patient</SelectItem>
                    <SelectItem value="supporter">Supporter</SelectItem>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="dispatcher">Dispatcher</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="hospital_partner_admin">Hospital Partner Admin</SelectItem>
                    <SelectItem value="employer_admin">Employer Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={editForm.language} onValueChange={(val) => setEditForm(prev => ({ ...prev, language: val }))}>
                  <SelectTrigger data-testid="select-edit-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="so">Somali</SelectItem>
                    <SelectItem value="hmn">Hmong</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} data-testid="button-cancel-edit-user">Cancel</Button>
            <Button
              onClick={handleEditSubmit}
              disabled={updateUserMutation.isPending || !editForm.firstName.trim() || !editForm.lastName.trim() || !editForm.email.trim()}
              data-testid="button-save-edit-user"
            >
              {updateUserMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewUser} onOpenChange={(open) => { if (!open) setViewUser(null); }}>
        <DialogContent data-testid="dialog-view-user">
          <DialogHeader>
            <DialogTitle data-testid="dialog-title-view-user">User Details</DialogTitle>
            <DialogDescription>Viewing profile for {viewUser?.username}</DialogDescription>
          </DialogHeader>
          {viewUser && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">First Name</p>
                  <p className="font-medium" data-testid="view-first-name">{viewUser.firstName || "-"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Last Name</p>
                  <p className="font-medium" data-testid="view-last-name">{viewUser.lastName || "-"}</p>
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="font-medium" data-testid="view-email">{viewUser.email || "-"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Username</p>
                <p className="font-medium font-mono" data-testid="view-username">{viewUser.username || "-"}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Role</p>
                  <div data-testid="view-role">{getRoleBadge(viewUser.role || "patient")}</div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Language</p>
                  <p className="font-medium" data-testid="view-language">{getLanguageLabel(viewUser.language || "en")}</p>
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">User ID</p>
                  <p className="font-mono text-xs" data-testid="view-user-id">{viewUser.id}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Joined</p>
                  <p className="text-sm" data-testid="view-created-at">
                    {viewUser.createdAt ? format(new Date(viewUser.createdAt), "MMM d, yyyy 'at' h:mm a") : "-"}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewUser(null)} data-testid="button-close-view-user">Close</Button>
            <Button onClick={() => { if (viewUser) { openEditDialog(viewUser); setViewUser(null); } }} data-testid="button-edit-from-view">
              <Edit className="w-4 h-4 mr-2" />
              Edit User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
